/**
 * @file useAttachmentPanelDrop.ts
 * @description Native drag-and-drop for the whole attachments panel, so a file
 * dropped anywhere attaches and a file dropped onto a row versions that row.
 * Consumer: `Attachments`, which spreads `containerHandlers` on the panel and
 * hands `onRowDragEnter`/`onRowDragLeave` to `AttachmentList`.
 *
 * `Upload.Dragger` cannot do this: rc-upload owns one drop surface and cannot
 * say which row the pointer is over. The dragger stays as the click-to-browse
 * strip and keeps handling its own drops; `Attachments` fences it behind an
 * element that stops the drop propagating, so the container never sees a drop
 * the strip already took and never uploads it twice.
 *
 * Every counter is a ref, never state. `dragenter` and `dragleave` fire once per
 * crossed element and bubble, so a leave that is really a move onto a child
 * would otherwise clear the highlight; a depth that returns to zero is the only
 * leave that means the pointer is gone. Rows count separately for the same
 * reason. `dropTargetFileUid` is mirrored into a ref because `dragenter` is a
 * continuous-priority update that a discrete `drop` does not flush, so the drop
 * reads the ref and only the highlight reads the state.
 *
 * When `enabled` is false every handler is literally `undefined`, so a read-only
 * panel registers no listener and prevents no default at all. A drag in flight
 * when `enabled` flips loses its `dragleave`, so the flip itself resets every
 * counter — during render, not in an effect.
 */
import { DragEvent, DragEventHandler, useCallback, useRef, useState } from 'react';

/**
 * Whether a drag carries files. `dataTransfer.items` is not readable while a
 * drag is in flight in every browser, but `types` always is, so text, links and
 * dragged table rows are told apart from files here and light nothing up.
 */
const carriesFiles = (event: DragEvent<HTMLElement>): boolean =>
    Array.from(event.dataTransfer?.types ?? []).includes('Files');

export interface AttachmentPanelDropOptions {
    /** False for a read-only or busy panel, which then registers no handler. */
    enabled: boolean;
    /**
     * Receives the dropped files and the file row the pointer was over, or null
     * when the drop landed on the panel background.
     */
    onFilesDropped: (files: File[], dropTargetFileUid: string | null) => void;
}

export interface AttachmentPanelDropContainerHandlers {
    onDragEnter?: DragEventHandler<HTMLElement>;
    onDragOver?: DragEventHandler<HTMLElement>;
    onDragLeave?: DragEventHandler<HTMLElement>;
    onDrop?: DragEventHandler<HTMLElement>;
}

export interface AttachmentPanelDrop {
    /** Whether a file drag is currently over the panel. */
    isDragging: boolean;
    /** The file row the pointer is over, or null for the panel background. */
    dropTargetFileUid: string | null;
    /** Spread on the panel's outermost element. */
    containerHandlers: AttachmentPanelDropContainerHandlers;
    /** Called by a row that can take a version, from its own `dragenter`. */
    onRowDragEnter?: (fileUid: string, event: DragEvent<HTMLElement>) => void;
    /** Called by the same row from its own `dragleave`. */
    onRowDragLeave?: (fileUid: string, event: DragEvent<HTMLElement>) => void;
}

export const useAttachmentPanelDrop = ({
    enabled,
    onFilesDropped,
}: AttachmentPanelDropOptions): AttachmentPanelDrop => {
    const dragDepth = useRef(0);
    const rowDragDepths = useRef(new Map<string, number>());
    const dropTargetFileUidRef = useRef<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dropTargetFileUid, setDropTargetFileUid] = useState<string | null>(null);
    const [enabledLastRender, setEnabledLastRender] = useState(enabled);

    if (enabledLastRender !== enabled) {
        setEnabledLastRender(enabled);
        dragDepth.current = 0;
        rowDragDepths.current.clear();
        dropTargetFileUidRef.current = null;
        setIsDragging(false);
        setDropTargetFileUid(null);
    }

    const endDrag = useCallback(() => {
        dragDepth.current = 0;
        rowDragDepths.current.clear();
        dropTargetFileUidRef.current = null;
        setIsDragging(false);
        setDropTargetFileUid(null);
    }, []);

    const handleDragEnter = useCallback((event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event)) return;
        event.preventDefault();
        dragDepth.current += 1;
        setIsDragging(true);
    }, []);

    const handleDragOver = useCallback((event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
    }, []);

    const handleDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event)) return;
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) {
            endDrag();
        }
    }, [endDrag]);

    const handleDrop = useCallback((event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event)) return;
        event.preventDefault();
        const files = Array.from(event.dataTransfer.files);
        const targetFileUid = dropTargetFileUidRef.current;
        endDrag();
        if (files.length === 0) return;
        onFilesDropped(files, targetFileUid);
    }, [endDrag, onFilesDropped]);

    const handleRowDragEnter = useCallback((fileUid: string, event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event)) return;
        const depths = rowDragDepths.current;
        depths.set(fileUid, (depths.get(fileUid) ?? 0) + 1);
        dropTargetFileUidRef.current = fileUid;
        setDropTargetFileUid(fileUid);
    }, []);

    const handleRowDragLeave = useCallback((fileUid: string, event: DragEvent<HTMLElement>) => {
        if (!carriesFiles(event)) return;
        const depths = rowDragDepths.current;
        const remaining = Math.max(0, (depths.get(fileUid) ?? 0) - 1);
        if (remaining > 0) {
            depths.set(fileUid, remaining);
            return;
        }
        depths.delete(fileUid);
        if (dropTargetFileUidRef.current !== fileUid) return;
        dropTargetFileUidRef.current = null;
        setDropTargetFileUid(null);
    }, []);

    if (!enabled) {
        return {
            isDragging: false,
            dropTargetFileUid: null,
            containerHandlers: {
                onDragEnter: undefined,
                onDragOver: undefined,
                onDragLeave: undefined,
                onDrop: undefined,
            },
            onRowDragEnter: undefined,
            onRowDragLeave: undefined,
        };
    }

    return {
        isDragging,
        dropTargetFileUid,
        containerHandlers: {
            onDragEnter: handleDragEnter,
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            onDrop: handleDrop,
        },
        onRowDragEnter: handleRowDragEnter,
        onRowDragLeave: handleRowDragLeave,
    };
};
